import { Component, OnDestroy, OnInit, Renderer2, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthenticationService } from '@app/core';
import { finalize } from 'rxjs/operators';
import { Subscription } from 'rxjs/Subscription';
import { RandomUsernameService } from 'app/core/random-username.service';
import { ModalService } from 'app/shared/modal.service';
import { TranslateService } from '@ngx-translate/core';
import { ErrorManagerService } from 'app/core/error-manager.service';
import { AlertService } from '@app/core/alert.service';
import * as amplitude from 'amplitude-js';
import { RecaptchaComponent } from 'ng-recaptcha';

@Component({
  selector: 'app-verify-phone-modal',
  templateUrl: './verify-phone-modal.template.html',
  styleUrls: ['./verify-phone-modal.template.scss'],
  animations: [],
})
export class VerifyPhoneModalComponent implements OnInit, OnDestroy {
  requestForm: FormGroup;
  confirmForm: FormGroup;
  loading = false;
  errors: string = null;
  success: string = null;
  userInfo$: Subscription = null;
  step = 0;
  phoneNumber: string;

  @ViewChild('code1') code1: any;
  @ViewChild('code2') code2: any;
  @ViewChild('code3') code3: any;
  @ViewChild('code4') code4: any;
  @ViewChild('code5') code5: any;
  @ViewChild('code6') code6: any;

  @ViewChild('captchaRef') captchaRef: RecaptchaComponent;

  constructor(
    private formBuilder: FormBuilder,
    private router: Router,
    public auth: AuthenticationService,
    private activatedRoute: ActivatedRoute,
    private nick: RandomUsernameService,
    private translate: TranslateService,
    private modalService: ModalService,
    private errorManager: ErrorManagerService,
    protected renderer: Renderer2,
    private alertService: AlertService
  ) {
    // super(elemRef, renderer);
  }

  public ngOnInit() {
    // super.ngOnInit();
    this.createForm();

    this.userInfo$ = this.auth.userInfo$.subscribe(
      (user: any) => {
        if (user && user.phoneVerified === true) {
          this.router.navigate(['/', { outlets: { modal: null } }]);
        }

        if (
          user &&
          user.phone &&
          user.phone.requestId &&
          new Date(user.phone.dateRequested).getTime() > new Date().getTime() - 300 * 1000
        ) {
          this.step = 1;
          this.phoneNumber = user.phone.phoneNumber;
        }
      },
      () => {}
    );
  }

  public ngOnDestroy() {
    if (this.userInfo$) {
      this.userInfo$.unsubscribe();
    }
  }

  public request($event: string): void {
    const code = this.requestForm.get('prefix').value + this.requestForm.get('number').value;
    if (code && code.length >= 6) {
      this.auth
        .phoneVerificationRequest(code, $event)
        .pipe(
          finalize(() => {
            this.loading = false;
          })
        )
        .subscribe(
          () => {
            this.step = 1;
            this.phoneNumber = code;
            this.alertService.success({
              title: 'Success!',
              text:
                'You will receive SMS shortly, if phone can not receive messages,' +
                ' we will call you and dictate code in couple of minutes.',
              timer: 10,
            });
          },
          response => {
            this.captchaRef.reset();
            this.alertService.error({
              title: 'Error!',
              text: response.json().message,
              timer: 10,
            });
          }
        );
    } else {
      this.loading = false;
      this.captchaRef.reset();
    }
  }

  public confirm($event: Event): void {
    $event.preventDefault();
    const code =
      this.confirmForm.get('code_1').value +
      this.confirmForm.get('code_2').value +
      this.confirmForm.get('code_3').value +
      this.confirmForm.get('code_4').value +
      this.confirmForm.get('code_5').value +
      this.confirmForm.get('code_6').value;
    if (!this.loading && code && code.length === 6) {
      this.loading = true;

      this.auth
        .phoneVerificationCheck(code)
        .pipe(
          finalize(() => {
            this.loading = false;
          })
        )
        .subscribe(
          response => {
            if (response.success) {
              amplitude.getInstance().logEvent('Onboarding - Verify Phone', {
                Success: true,
              });
              this.step = 1;
              this.alertService.success({
                title: 'Success!',
                text: 'Your phone number was verified!',
                timer: 10,
              });

              this.auth.getUserInfo().subscribe(
                () => {
                  this.router.navigate(['/', { outlets: { modal: null } }]);
                  // this.modalService.openPhoneBonuses();
                },
                () => {}
              );
            } else {
              amplitude.getInstance().logEvent('Onboarding - Verify Phone', {
                Success: false,
              });
              this.alertService.error({
                title: 'Error!',
                text: response.message,
                timer: 10,
              });
              if (response.reset) {
                this.step = 0;
              }
            }
          },
          response => {
            amplitude.getInstance().logEvent('Onboarding - Verify Phone', {
              Success: false,
            });
            this.alertService.error({
              title: 'Error!',
              text: response.json().message,
              timer: 10,
            });
          }
        );
    }
  }

  public cancelVerification($event: Event): void {
    $event.preventDefault();
    this.auth.phoneVerificationCancel().subscribe();
  }

  public skip($event: Event): void {
    amplitude.getInstance().logEvent('Onboarding - Skip Phone Verification');
    $event.preventDefault();
    if (this.activatedRoute.snapshot.queryParams.onboarding) {
      amplitude.getInstance().logEvent('Onboarding - View deposit options');
      this.router.navigate(['/', { outlets: { modal: 'deposit' } }], { queryParams: { onboarding: true } });
    } else {
      this.router.navigate(['/', { outlets: { modal: null } }]);
    }
  }

  private createForm(): void {
    this.requestForm = this.formBuilder.group({
      prefix: ['', [Validators.required]],
      number: ['', [Validators.required]],
    });

    this.confirmForm = this.formBuilder.group({
      code_1: ['', [Validators.required, Validators.minLength(1), Validators.maxLength(1)]],
      code_2: ['', [Validators.required, Validators.minLength(1), Validators.maxLength(1)]],
      code_3: ['', [Validators.required, Validators.minLength(1), Validators.maxLength(1)]],
      code_4: ['', [Validators.required, Validators.minLength(1), Validators.maxLength(1)]],
      code_5: ['', [Validators.required, Validators.minLength(1), Validators.maxLength(1)]],
      code_6: ['', [Validators.required, Validators.minLength(1), Validators.maxLength(1)]],
    });
  }

  codeChange(code: any, el: any = null) {
    if (code.length === 6) {
      this.confirmForm.patchValue({ code_1: code[0] });
      this.confirmForm.patchValue({ code_2: code[1] });
      this.confirmForm.patchValue({ code_3: code[2] });
      this.confirmForm.patchValue({ code_4: code[3] });
      this.confirmForm.patchValue({ code_5: code[4] });
      this.confirmForm.patchValue({ code_6: code[5] });
    } else if (code.length === 1 && el) {
      el.element.nativeElement.children[0].focus();
    }
  }
}



// WEBPACK FOOTER //
// /var/lib/jenkins/workspace/Prod_web-client/src/app/_v2/template/modals/signup/verify-phone/verify-phone-modal.component.ts